import { message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { generateInterviewGuideViaEvents } from '@/api/assessments/api';
import type { EventJobEventResponseDTO } from '@/api/events/api';
import {
  GENERATION_PHASE_STEPS,
  GenerationPhase,
  interviewGuideContextPackStorageKey,
} from '@/utils/assessmentInterviewFlow';
import { BUNDLED_INTERVIEW_GUIDE_TEMPLATE_KEY } from '@/utils/interviewGuideSkillFilter';

/** Dedupes scoped generation when React Strict Mode runs effects twice (dev) or deps churn. */
const scopedInterviewGuideGenerationByAssessmentId = new Map<string, Promise<void>>();

function readTemplateKeyFromSession(assessmentInstanceId: string): string {
  const key = `bdp.interviewPrep.guideSelection.${assessmentInstanceId}`;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return BUNDLED_INTERVIEW_GUIDE_TEMPLATE_KEY;
    }
    const parsed = JSON.parse(raw) as { template_key?: string; skill_version_id?: string };
    if (parsed.template_key && String(parsed.template_key).trim()) {
      return String(parsed.template_key).trim();
    }
  } catch {
    // ignore
  }
  return BUNDLED_INTERVIEW_GUIDE_TEMPLATE_KEY;
}

export interface UseStepGeneratingResult {
  currentPhase: GenerationPhase | null;
  liveProgress: number;
  runScopedGeneration: (force?: boolean) => Promise<void>;
}

/**
 * Orchestrates scoped interview-guide generation via the event-stream API.
 * Dedupes concurrent calls per assessment instance to avoid double-triggering
 * in React Strict Mode.
 */
export function useStepGenerating(
  isScoped: boolean,
  assessmentInstanceId: string | undefined,
  accessToken: string | null,
  clientId: string | undefined,
  projectId: string | undefined,
  onScopedSuccess: (() => void) | undefined,
  onScopedFailure: (() => void) | undefined,
): UseStepGeneratingResult {
  const completedRef = useRef(false);
  const [currentPhase, setCurrentPhase] = useState<GenerationPhase | null>(null);
  const [liveProgress, setLiveProgress] = useState(0);

  const handleEvent = useCallback((event: EventJobEventResponseDTO) => {
    flushSync(() => {
      const phase = event.phase as GenerationPhase | null;
      if (phase && GENERATION_PHASE_STEPS.some((s) => s.phase === phase)) {
        setCurrentPhase(phase);
      }
      if (typeof event.progress === 'number') {
        setLiveProgress(event.progress);
      }
    });
  }, []);

  const runScopedGeneration = useCallback(
    async (force = false): Promise<void> => {
      if (completedRef.current && !force) {
        return;
      }
      if (force) {
        completedRef.current = false;
        setCurrentPhase(null);
        setLiveProgress(0);
      }

      const instanceId = assessmentInstanceId?.trim() ?? '';

      if (!accessToken || !clientId || !projectId || !instanceId) {
        completedRef.current = true;
        void message.error(
          'Sign in and open this assessment from a project to generate the guide.',
        );
        onScopedFailure?.();
        return;
      }

      const templateKey = readTemplateKeyFromSession(instanceId);
      try {
        const res = await generateInterviewGuideViaEvents(
          instanceId,
          { template_key: templateKey },
          accessToken,
          clientId,
          projectId,
          handleEvent,
        );
        try {
          sessionStorage.setItem(
            interviewGuideContextPackStorageKey(instanceId),
            res.context_pack_status,
          );
        } catch {
          // ignore quota / private mode
        }
        completedRef.current = true;
        onScopedSuccess?.();
      } catch (err) {
        completedRef.current = false;
        const detail = err instanceof Error ? err.message : 'Interview guide generation failed.';
        void message.error(detail);
        onScopedFailure?.();
      }
    },
    [
      accessToken,
      assessmentInstanceId,
      clientId,
      handleEvent,
      onScopedFailure,
      onScopedSuccess,
      projectId,
    ],
  );

  const runScopedGenerationRef = useRef(runScopedGeneration);
  runScopedGenerationRef.current = runScopedGeneration;

  useEffect(() => {
    if (!isScoped) {
      return undefined;
    }
    const instanceId = assessmentInstanceId?.trim() ?? '';
    if (!instanceId) {
      return undefined;
    }
    const existing = scopedInterviewGuideGenerationByAssessmentId.get(instanceId);
    if (existing) {
      return undefined;
    }
    const pending = runScopedGenerationRef.current(false).finally(() => {
      scopedInterviewGuideGenerationByAssessmentId.delete(instanceId);
    });
    scopedInterviewGuideGenerationByAssessmentId.set(instanceId, pending);
    return undefined;
  }, [assessmentInstanceId, isScoped]);

  return { currentPhase, liveProgress, runScopedGeneration };
}
